import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import '../App.css';
import {
  // ComputerDesktopIcon,
  // BanknotesIcon,
  CpuChipIcon,
  // AcademicCapIcon,
  // HeartIcon,
  UserGroupIcon,
  RocketLaunchIcon,
  CodeBracketIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

// Declare calendar type for Google Calendar widget
declare global {
  interface Window {
    calendar: {
      schedulingButton: {
        open: (options: { url: string; color: string; label: string }) => void;
      };
    };
  }
}

const SERVICES = [
  {
    title: 'AI & Data Intelligence',
    desc: 'AI reshapes industries with smart solutions, and data fuels innovation.',
    img: './Images/slice/slice5.png',
    link: '/ai-and-data',
  },
  {
    title: 'Engineering',
    desc: 'From planning to launch, we deliver end-to-end engineering of secure software.',
    img: './Images/slice/img3.png',
    link: '/engineering',
  },
  {
    title: 'RnD',
    desc: 'Designing scalable, user-focused products that accelerate business success.',
    img: './Images/slice/slice6.png',
    link: '/engineering',
  },
  {
    title: 'Technology Consulting',
    desc: 'Expert guidance that turns vision into execution and impact.',
    img: './Images/slice/sclice1.png',
    link: '/engineering',
  },
  {
    title: 'Team Augmentation',
    desc: 'Scale faster with experienced engineers embedded into your team.',
    img: './Images/slice/img2.png',
    link: '/engineering',
  },
];

function HeroSection() {
  return (
    <section className="relative overflow-hidden pb-28 pt-32 sm:pb-32 sm:pt-36 lg:pb-36 lg:pt-40">
      {/* Background blobs */}
      <div className="absolute inset-0 -z-10">
        {/* Purple blob with floating animation */}
        <motion.span
          className="top-2/5 absolute -left-32 h-96 w-96 rounded-full bg-purple-500/25 blur-3xl dark:bg-purple-600/30"
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Light purple accent blob */}
        <motion.span
          className="absolute left-3/4 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-pink-400/15 blur-2xl dark:bg-pink-500/20"
          animate={{
            x: [0, -25, 0],
            y: [0, 15, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      </div>

      <div className="mx-auto max-w-6xl px-6 text-center">
        {/* Main Heading with motion */}
        <motion.h1
          className="mb-8 text-4xl font-extrabold leading-tight tracking-tight text-gray-800 sm:text-5xl lg:text-6xl dark:text-gray-100"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          Engineering AI for{' '}
          <motion.span
            className="block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-[length:200%_200%] bg-clip-text text-transparent"
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            Human Empowerment
          </motion.span>
        </motion.h1>

        {/* Subheading with motion */}
        <motion.p
          className="mx-auto mb-12 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg lg:text-xl dark:text-gray-500/90"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
        >
          AI-driven systems built for real industries. Practical intelligence that helps teams move
          faster and think clearer.
        </motion.p>

        {/* Action Buttons with staggered animation */}
        <motion.div
          className="flex flex-col items-center gap-6 sm:flex-row sm:justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
        >
          {/* Book a Meeting */}
          <motion.a
            href="/contacts"
            className="after:animate-shimmer relative overflow-hidden rounded-full border border-white/20 bg-gradient-to-r from-purple-800/60 via-pink-700/50 to-purple-900/60 px-8 py-3 text-sm font-medium text-white shadow-lg backdrop-blur-lg transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-white/10 before:opacity-0 after:absolute after:-left-16 after:top-0 after:h-full after:w-16 after:rotate-45 after:bg-gradient-to-r after:from-white/10 after:via-white/20 after:to-white/10 hover:shadow-[0_10px_30px_rgba(168,85,247,0.5)] hover:before:opacity-20"
            whileHover={{
              scale: 1.05,
              boxShadow: '0 10px 30px rgba(168,85,247,0.5)',
            }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            Book a Meeting
          </motion.a>

          {/* Explore Our Work */}
          <motion.a
            href="/success-stories"
            className="after:animate-shimmer relative overflow-hidden rounded-full border border-white/20 bg-gradient-to-r from-indigo-800/60 via-purple-700/50 to-indigo-900/60 px-8 py-3 text-sm font-medium text-white shadow-lg backdrop-blur-lg transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-white/10 before:opacity-0 after:absolute after:-left-16 after:top-0 after:h-full after:w-16 after:rotate-45 after:bg-gradient-to-r after:from-white/10 after:via-white/20 after:to-white/10 hover:shadow-[0_10px_30px_rgba(99,102,241,0.5)] hover:before:opacity-20"
            whileHover={{
              scale: 1.05,
              boxShadow: '0 10px 30px rgba(99,102,241,0.5)',
            }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            Explore Our Work
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

function WhatWeDoTimeline() {
  const services = [
    {
      title: 'Engineering',
      short: 'Build Products',
      desc: 'Full-cycle product development: Web, Mobile, AI, and cloud-native solutions.',
      icon: CodeBracketIcon,
      href: '/engineering',
      gradient: 'from-indigo-400 via-purple-400 to-pink-400',
    },
    {
      title: 'Consulting',
      short: 'Strategize Growth',
      desc: 'Strategic guidance for tech adoption, architecture, and scaling products globally.',
      icon: RocketLaunchIcon,
      href: '/consulting',
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    },
    {
      title: 'Team Augmentation',
      short: 'Scale Fast',
      desc: 'Scale your engineering capacity instantly with our vetted remote teams.',
      icon: UsersIcon,
      href: '/hire-tech-team',
      gradient: 'from-yellow-400 via-orange-400 to-red-400',
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' as const } },
  };

  const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.2 } },
  };

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-32">
      <div className="relative flex flex-col items-start gap-16 md:flex-row md:gap-24">
        {/* Left Side - About */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex-1"
        >
          <motion.h2
            className="mb-10 text-5xl font-extrabold leading-snug tracking-tight text-gray-900 sm:text-6xl lg:text-7xl dark:text-gray-100"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Build <br />
            <span className="animate-gradientText bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Greater
            </span>
            <br /> Together
          </motion.h2>

          <motion.p
            className="mb-10 text-base leading-relaxed text-gray-700 sm:text-base dark:text-gray-300"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Partner with world-class engineering teams, cutting-edge Technology solutions and
            strategic technical expertise. We transform visionary ideas into robust, market-ready
            products that drive real impact.
          </motion.p>

          <motion.a
            href="/about"
            className="inline-block rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-6 py-3 font-semibold text-white shadow-lg transition-transform hover:scale-105"
            whileHover={{ scale: 1.05 }}
          >
            Learn More
          </motion.a>
        </motion.div>

        {/* Right Side - Timeline / Services */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="relative flex-1"
        >
          {/* Big Background Words */}
          <motion.div
            className="absolute -top-10 left-0 text-6xl font-extrabold leading-tight text-gray-100/10 sm:text-7xl md:text-8xl dark:text-white/5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            ENGINEERING <br /> CONSULTING <br /> AUGMENTATION
          </motion.div>

          {/* Connector Line */}
          <div className="absolute left-10 top-16 hidden h-[calc(100%-4rem)] w-1 rounded-full bg-gradient-to-b from-indigo-400 to-pink-400 opacity-20 md:block" />

          <div className="relative flex flex-col gap-12">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.a
                  key={i}
                  href={s.href}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  className="group relative flex items-start gap-6 rounded-3xl border p-6 shadow-lg backdrop-blur transition hover:shadow-2xl dark:border-white/10 dark:bg-white/5"
                >
                  {/* Gradient corner bar */}
                  <div
                    className={`absolute left-0 top-0 h-1 w-1/5 rounded-tr-xl bg-gradient-to-r ${s.gradient}`}
                  />

                  {/* Icon */}
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r ${s.gradient} flex-shrink-0 text-white shadow-xl`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="mb-1 text-xl font-semibold text-gray-900 sm:text-2xl dark:text-gray-100">
                      {s.title}
                    </h3>
                    <p className="text-sm text-gray-600 sm:text-base dark:text-gray-300">
                      {s.desc}
                    </p>
                  </div>

                  {/* Hover dot on right corner */}
                  <div className="absolute right-4 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-gradient-to-r from-indigo-400 to-pink-400 opacity-0 transition group-hover:opacity-100" />

                  {/* Hover glow overlay */}
                  <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10 opacity-0 transition duration-300 group-hover:opacity-30" />
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ClientsSection() {
  const clients = [
    {
      src: 'Images/Clients/Dialog-logo (2).png',
      alt: 'Dialog Axiata PLC',
      href: 'https://www.dialog.lk/',
    },
    {
      src: 'Images/Clients/upresponse-logo.png',
      alt: 'UpResponse.io',
      href: 'https://upresponse.io/',
    },
    {
      src: 'Images/Clients/Fitness-Leaf-AI-logo-Transparent-bg-400x100-1.png',
      alt: 'Fitness Leaf AI',
    },
    {
      src: 'Images/Clients/hagos_marketing_logo-_blue_transparent_background__1_.png',
      alt: 'Hagos Marketing',
    },
    {
      src: 'Images/Clients/otago-polytechnic-horizontal-blue.png',
      alt: 'Otago Polytechnic',
      href: 'https://www.otago.ac.nz/',
    },
    { src: 'Images/Clients/orpheus-digital.png', alt: 'Orpheus Digital' },
    {
      src: 'Images/Clients/jaar-law-favicon.png',
      alt: 'JAAR Law',
      href: 'https://www.jaarlaw.com/',
    },
  ];

  return (
    <section className="flex w-full justify-center overflow-hidden px-4 py-24">
      <motion.div
        className="relative w-full max-w-6xl rounded-3xl border border-white/20 bg-gradient-to-r from-purple-900/30 via-blue-400/40 to-pink-900/30 p-10 shadow-lg shadow-purple-500/20 backdrop-blur-xl sm:p-16"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        {/* Section Header */}
        <motion.h4
          className="mb-10 text-center text-lg font-semibold text-gray-800 sm:text-2xl md:text-3xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <span className="animate-gradientText text-white dark:text-gray-300 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent dark:bg-gradient-to-r dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400">
            Trusted by {' '}
            <span className="mx-1 inline-block animate-gradientText bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            Organizations 
            </span>
          {' '}  Worldwide
          </span>
        </motion.h4>

        {/* Clients grid */}
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
          {clients.map((client, index) => {
            const content = (
              <>
                <img
                  src={client.src}
                  alt={client.alt}
                  className="h-12 object-contain sm:h-16 md:h-20 lg:h-24"
                />
                <span className="mt-2 text-center text-xs font-medium text-gray-800 sm:text-sm md:text-base dark:text-gray-300">
                  {client.alt}
                </span>
              </>
            );

            return (
              <motion.div
                key={client.alt}
                className="flex transform flex-col items-center justify-center rounded-2xl p-4 transition-all duration-500 hover:bg-white/40"
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{
                  scale: 1.05,
                  y: -5,
                  transition: { type: 'spring', stiffness: 400, damping: 17 },
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: 'easeOut',
                }}
                viewport={{ once: true }}
              >
                {client.href ? (
                  <a
                    href={client.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center"
                  >
                    {content}
                  </a>
                ) : (
                  content
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Subtext */}
        <motion.p
          className="mx-auto mt-8 max-w-3xl text-center text-sm text-gray-600 sm:text-base dark:text-gray-400 sm:mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          Collaborating with clients across industries like Telecommunications, Healthcare, FinTech,
          Marketing, and Education.
        </motion.p>
      </motion.div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24">
      <motion.h2
        className="mb-12 text-center text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        All in{' '}
        <motion.span
          className="animate-gradientText bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          One{' '}
        </motion.span>{' '}
        Powerhouse
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={24}
          slidesPerView={1}
          loop
          pagination={{ clickable: true }}
          autoplay={{ delay: 4500 }}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {SERVICES.map((service, index) => (
            <SwiperSlide key={service.title}>
              <motion.a
                href={service.link}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/20 bg-white/30 p-4 shadow-lg backdrop-blur-md transition-all duration-300 hover:bg-white/50 hover:shadow-xl"
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  transition: { type: 'spring', stiffness: 300, damping: 20 },
                }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: 'easeOut',
                }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="mb-4 h-56 overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.img
                    src={service.img}
                    alt={service.title}
                    className="h-full w-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </motion.div>

                <motion.h3
                  className="mb-2 text-2xl font-semibold text-gray-800 dark:text-gray-100"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  {service.title}
                </motion.h3>

                <motion.p
                  className="text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  {service.desc}
                </motion.p>

                {/* Animated arrow indicator */}
              </motion.a>
            </SwiperSlide>
          ))}
        </Swiper>
      </motion.div>
    </section>
  );
}

// new function as we color areas such as telcom, fintech, AI, education etc differently later on
// function IndustrySection() {
//   const industries = [
//     {
//       name: 'Telecommunications',
//       icon: ComputerDesktopIcon,
//       color: 'from-purple-300 via-purple-400 to-purple-500', // soft pastel purple
//     },
//     {
//       name: 'FinTech',
//       icon: BanknotesIcon,
//       color: 'from-blue-300 via-blue-400 to-blue-500', // muted professional blue
//     },
//     {
//       name: 'Artificial Intelligence',
//       icon: CpuChipIcon,
//       color: 'from-pink-300 via-pink-400 to-pink-500', // soft pink for innovation/AI
//     },
//     {
//       name: 'Education',
//       icon: AcademicCapIcon,
//       color: 'from-green-300 via-green-400 to-green-500', // pastel green, calm and professional
//     },
//     {
//       name: 'Healthcare',
//       icon: HeartIcon,
//       color: 'from-red-300 via-red-400 to-red-500', // muted red for health
//     },
//   ];

//   return (
//     <section className="mx-auto max-w-6xl px-6 py-24">
//       <div className="mx-auto max-w-6xl">
//         {/* Section heading */}
//         <h3 className="mb-12 text-center text-3xl font-bold text-gray-800 sm:text-4xl">
//           <span className="animate-gradientText bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
//             Industries We Innovate In
//           </span>
//         </h3>

//         {/* Industry cards */}
// <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5">
//   {industries.map((industry) => {
//     const Icon = industry.icon;
//     return (
//       <div
//         key={industry.name}
//         className={`relative flex flex-col items-center justify-center rounded-3xl bg-gradient-to-br ${industry.color} border border-white/20 bg-opacity-20 shadow-lg backdrop-blur-xl transition-transform duration-300 hover:scale-105 p-6`}
//       >
//         <div className="mb-3 rounded-full bg-white/10 p-3">
//           <Icon className="h-10 w-10 text-white" />
//         </div>
//         <span className="text-center text-lg font-semibold text-gray-800 dark:text-gray-100">
//           {industry.name}
//         </span>
//       </div>
//     );
//   })}
// </div>

//         {/* Animated glow effect button to link our engineering page in the center of div */}
//         <div className="mt-12 flex justify-center">
//           <a
//             href="/engineering"
//             className="after:animate-shimmer relative overflow-hidden rounded-full border border-white/20 bg-gradient-to-r from-purple-800/60 via-pink-700/50 to-purple-900/60 px-8 py-3 text-sm font-medium text-white shadow-lg backdrop-blur-lg transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-white/10 before:opacity-0 after:absolute after:-left-16 after:top-0 after:h-full after:w-16 after:rotate-45 after:bg-gradient-to-r after:from-white/10 after:via-white/20 after:to-white/10 hover:scale-105 hover:shadow-[0_10px_30px_rgba(168,85,247,0.5)] hover:before:opacity-20"
//           >
//             Explore Our Engineering Solutions
//           </a>
//         </div>
//       </div>
//     </section>
//   );
// }

function HumanFirstEngineering() {
  const features = [
    {
      icon: UserGroupIcon,
      title: 'Human-Centric Approach',
      description:
        'We prioritize people first: our solutions empower users, improve workflows, and focus on real human needs, not just technology.',
      color: 'from-purple-500 to-pink-500',
      gradient: 'from-purple-400 via-pink-400 to-red-400',
    },
    {
      icon: CpuChipIcon,
      title: 'Engineering Excellence',
      description:
        'Our team combines deep technical expertise with practical intelligence to build scalable, reliable, and maintainable systems.',
      color: 'from-indigo-500 to-blue-500',
      gradient: 'from-indigo-400 via-blue-400 to-green-400',
    },
    {
      icon: RocketLaunchIcon,
      title: 'Impactful Solutions',
      description:
        'We design and deploy products that drive measurable outcomes, helping businesses grow, innovate, and succeed.',
      color: 'from-teal-400 to-cyan-500',
      gradient: 'from-green-400 via-teal-400 to-blue-500',
    },
  ];

  return (
    <section className="rounded-3x relative mx-auto max-w-6xl px-6 py-24">
      {/* Background blobs */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow with floating animation */}
        <motion.div
          className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]"
          animate={{
            background: [
              'radial-gradient(circle at 70% 30%, rgba(139,92,246,0.18), transparent 60%)',
              'radial-gradient(circle at 30% 70%, rgba(139,92,246,0.22), transparent 60%)',
              'radial-gradient(circle at 70% 30%, rgba(139,92,246,0.18), transparent 60%)',
            ],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        {/* Animated Grid */}
        <motion.div
          className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]"
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <div className="mx-auto max-w-6xl px-6 text-center">
        {/* Animated heading */}
        <motion.h2
          className="text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          Get{' '}
          <motion.span
            className="animate-gradientText bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent"
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            Human First,
          </motion.span>{' '}
          Engineering Team
        </motion.h2>

        {/* Animated description */}
        <motion.p
          className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          Our philosophy combines empathy with engineering. We craft solutions that respect humans,
          harness cutting-edge technology, and deliver real-world impact.
        </motion.p>

        {/* Features with staggered animations */}
        <motion.div
          className="mt-16 grid gap-8 text-left sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          transition={{ staggerChildren: 0.2, delayChildren: 0.1 }}
          viewport={{ once: true }}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;

            return (
              <motion.div
                key={feature.title}
                className="group relative overflow-hidden rounded-2xl border border-white/20 bg-white/80 p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-gray-900/70"
                variants={{
                  hidden: { opacity: 0, y: 50, scale: 0.95 },
                  visible: { opacity: 1, y: 0, scale: 1 },
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  transition: { type: 'spring', stiffness: 300, damping: 20 },
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                {/* Animated gradient accent line */}
                <motion.div
                  className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${feature.gradient}`}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.2 + 0.5 }}
                  viewport={{ once: true }}
                  style={{ transformOrigin: 'left' }}
                />

                {/* Animated soft hover glow */}
                <motion.div
                  className={`pointer-events-none absolute -inset-6 bg-gradient-to-r opacity-0 blur-3xl transition-opacity duration-500 ${feature.gradient}`}
                  whileHover={{ opacity: 0.3 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Animated Icon */}
                <motion.div
                  className={`relative mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} text-white shadow-md`}
                  initial={{ rotate: 0, scale: 1 }}
                  whileHover={{
                    rotate: [0, -10, 10, 0],
                    scale: 1.1,
                    transition: { duration: 0.5 },
                  }}
                  whileInView={{
                    scale: [0.8, 1.2, 1],
                    rotate: [0, 360, 0],
                  }}
                  transition={{
                    duration: 0.8,
                    delay: index * 0.3 + 0.6,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    animate={{
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: index * 0.5,
                    }}
                  >
                    <Icon className="h-6 w-6" />
                  </motion.div>
                </motion.div>

                {/* Animated Content */}
                <motion.h3
                  className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.8 }}
                  viewport={{ once: true }}
                >
                  {feature.title}
                </motion.h3>

                <motion.p
                  className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 1 }}
                  viewport={{ once: true }}
                >
                  {feature.description}
                </motion.p>

                {/* Animated border pulse on hover */}
                <motion.div
                  className="absolute inset-0 rounded-2xl border-2 border-transparent"
                  whileHover={{
                    borderColor: `rgb(139 92 246 / 0.3)`,
                    boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)',
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
function OneCallAway() {
  return (
    <section className="relative mx-auto max-w-6xl px-6 py-24">
      {/* Soft background glow with animation */}
      <div className="absolute inset-0 -z-10">
        <motion.div
          className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.15),transparent_70%)]"
          animate={{
            background: [
              'radial-gradient(circle at 50% 50%, rgba(139,92,246,0.15), transparent 70%)',
              'radial-gradient(circle at 60% 40%, rgba(99,102,241,0.18), transparent 70%)',
              'radial-gradient(circle at 40% 60%, rgba(236,72,153,0.15), transparent 70%)',
              'radial-gradient(circle at 50% 50%, rgba(139,92,246,0.15), transparent 70%)',
            ],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <div className="mx-auto max-w-4xl text-center">
        {/* Animated heading */}
        <motion.h2
          className="text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          You're{' '}
          <motion.span
            className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent"
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            one call away
          </motion.span>
        </motion.h2>

        {/* Animated description */}
        <motion.p
          className="mx-auto mt-5 max-w-2xl text-lg text-gray-600 dark:text-gray-300"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          Whether you want to build with us or talk strategy with leadership, there is a clear next
          step.
        </motion.p>

        {/* Animated cards container */}
        <motion.div
          className="mt-12 grid gap-6 sm:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          transition={{ staggerChildren: 0.2, delayChildren: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Join the team card */}
          <motion.a
            href="/join-us"
            className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 text-left shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700 dark:bg-gray-900"
            variants={{
              hidden: { opacity: 0, y: 50, scale: 0.95 },
              visible: { opacity: 1, y: 0, scale: 1 },
            }}
            whileHover={{
              y: -8,
              scale: 1.02,
              transition: { type: 'spring', stiffness: 300, damping: 20 },
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            {/* Subtle animated border on hover */}
            <motion.div
              className="absolute inset-0 rounded-2xl border-2 border-transparent"
              whileHover={{
                borderColor: `rgb(99 102 241 / 0.3)`,
                boxShadow: '0 0 20px rgba(99, 102, 241, 0.1)',
              }}
              transition={{ duration: 0.3 }}
            />

            <motion.h3
              className="text-xl font-bold text-gray-900 dark:text-gray-100"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              Want to join us?
            </motion.h3>

            <motion.p
              className="mt-3 text-sm text-gray-600 dark:text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              viewport={{ once: true }}
            >
              Work on real products, learn fast, and grow with an engineering-led team focused on
              impact.
            </motion.p>

            <motion.span
              className="mt-4 inline-flex items-center text-sm font-semibold text-indigo-600 group-hover:underline dark:text-indigo-400"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              viewport={{ once: true }}
            >
              View open roles
              <motion.span
                className="ml-1"
                animate={{ x: [0, 4, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                →
              </motion.span>
            </motion.span>
          </motion.a>

          {/* Call with CEO card */}
          <motion.a
            href="/contacts"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-8 text-left shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(99,102,241,0.45)]"
            variants={{
              hidden: { opacity: 0, y: 50, scale: 0.95 },
              visible: { opacity: 1, y: 0, scale: 1 },
            }}
            whileHover={{
              y: -8,
              scale: 1.02,
              transition: { type: 'spring', stiffness: 300, damping: 20 },
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            {/* Animated shimmer effect */}
            <motion.div
              className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-white/0 via-white/10 to-white/0"
              whileHover={{
                translateX: '100%',
                transition: { duration: 0.8, ease: 'easeInOut' },
              }}
            />

            <motion.h3
              className="text-xl font-bold text-white"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              Want to talk with our CEO?
            </motion.h3>

            <motion.p
              className="mt-3 text-sm text-white/90"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              viewport={{ once: true }}
            >
              Discuss ideas, partnerships, or complex problems directly with the person shaping the
              vision.
            </motion.p>

            <motion.span
              className="mt-4 inline-flex items-center text-sm font-semibold text-white underline-offset-4 group-hover:underline"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              viewport={{ once: true }}
            >
              Schedule a call
              <motion.span
                className="ml-1"
                animate={{ x: [0, 4, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                →
              </motion.span>
            </motion.span>
          </motion.a>
        </motion.div>

        {/* Animated footer text */}
        <motion.p
          className="mt-8 text-sm text-gray-500 dark:text-gray-400"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          No pressure. No middle layers. Just honest conversations.
        </motion.p>
      </div>
    </section>
  );
}

export default function Home() {
  document.title =
    'Akwid Labs | Top Sri Lankan AI and Engineering Company to Build Your Product with';
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute(
      'content',
      'Akwid Labs is a top Sri Lankan AI and engineering company that builds practical, impactful products. We combine human-centric design with cutting-edge technology to help businesses innovate and grow. Contact us to build your next product with our expert team.',
    );
  }

  return (
    <main className="from-slate-150 relative min-h-screen bg-gradient-to-br via-violet-100 to-indigo-100 transition-colors duration-500">
      <HeroSection />
      <WhatWeDoTimeline />
      <ServicesSection />
      <ClientsSection />

      {/* <IndustrySection /> */}
      <HumanFirstEngineering />
      <OneCallAway />
    </main>
  );
}
